import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Palette, Menu, Search, Languages, FileText } from 'lucide-react';
import { settingsApi } from '../../api/endpoints/settings';
import { Tabs } from '../../components/ui/Tabs';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../hooks/usePermissions';
import { ThemeTab } from './tabs/ThemeTab';
import { NavbarTab } from './tabs/NavbarTab';
import { SeoTab } from './tabs/SeoTab';
import { LanguagesTab } from './tabs/LanguagesTab';
import { PagesTab } from './tabs/PagesTab';
import type { StoreSettings } from '../../types/settings';

export function StoreDesignPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { can } = usePermissions();
  // Tab persists in the URL so /design?tab=pages deep-links into the Pages tab
  // and the legacy /pages → redirect can land users where they expect.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'pages';
  const setActiveTab = (key: string) => setSearchParams({ tab: key }, { replace: true });

  const { data, isLoading } = useQuery({
    queryKey: ['settings', storeId],
    queryFn: () => settingsApi.get(storeId!),
    enabled: !!storeId,
  });

  // Permissions mirror the backend write endpoints: the storefront editor
  // (pages/theme/menus/languages) is gated on 'pages.edit', SEO on
  // 'settings.editSeo'. Without these, any store member saw the full design
  // editor and only hit a 403 on save.
  const tabs = [
    { key: 'pages', label: t('sidebar.pages', 'الصفحات'), icon: <FileText className="h-4 w-4" />, permission: 'pages.edit' },
    { key: 'theme', label: t('settingsTheme.title', 'المظهر'), icon: <Palette className="h-4 w-4" />, permission: 'pages.edit' },
    { key: 'navbar', label: t('settingsNavbar.title', 'شريط التنقل'), icon: <Menu className="h-4 w-4" />, permission: 'pages.edit' },
    { key: 'seo', label: t('settings.seo', 'SEO'), icon: <Search className="h-4 w-4" />, permission: 'settings.editSeo' },
    { key: 'languages', label: t('settings.languages', 'اللغات'), icon: <Languages className="h-4 w-4" />, permission: 'pages.edit' },
  ];

  const visibleTabs = useMemo(
    () => tabs.filter(tab => !('permission' in tab) || can((tab as any).permission)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can, t],
  );

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(tab => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key ?? 'theme');
    }
  }, [visibleTabs, activeTab]);

  if (isLoading) return <LoadingSpinner fullPage />;

  const settings = data?.settings as StoreSettings | undefined;

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">{t('sidebar.storeDesign', 'تصميم المتجر')}</h1>
      <Tabs tabs={visibleTabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6 flex-wrap" />
      <div className="mt-6">
        {visibleTabs.length === 0 ? (
          <div className="text-center text-text-secondary py-12">
            {t('common.noAccess', 'لا تملك صلاحية الوصول لهذا القسم')}
          </div>
        ) : !visibleTabs.some(tab => tab.key === activeTab) ? null : (
          <>
            {activeTab === 'theme' && <ThemeTab settings={settings} storeId={storeId!} />}
            {activeTab === 'navbar' && <NavbarTab settings={settings} storeId={storeId!} />}
            {activeTab === 'pages' && <PagesTab storeId={storeId!} />}
            {activeTab === 'seo' && <SeoTab settings={settings} storeId={storeId!} />}
            {activeTab === 'languages' && <LanguagesTab settings={settings} storeId={storeId!} />}
          </>
        )}
      </div>
    </div>
  );
}
